/**
 * Узкий вьюпорт — переключатель **раскладки**, а не устройства (задача 114 §3.1).
 *
 * Отличать от `lib/device.isMobileDevice()`: та отвечает на вопрос «это телефон»
 * (UA + `pointer: coarse`) и решает, КУДА пускать пользователя при входе —
 * поэтому она намеренно не смотрит на ширину, чтобы офисный на узком окне не
 * залипал в мобильном мире. Здесь вопрос другой: «поместится ли таблица», и
 * ответ на него даёт именно ширина. `/work-status` — один роут на обе
 * поверхности (§3.1), поэтому переключение раскладки живёт в самой странице.
 *
 * Реализовано через `useSyncExternalStore`, а не `useState` + эффект:
 * `matchMedia` — внешнее хранилище, и синхронный `setState` внутри эффекта даёт
 * каскадный ререндер (ловится правилом React Compiler). Заодно снимается
 * рассинхрон между первым рендером и моментом подписки.
 */
import { useCallback, useSyncExternalStore } from "react";

/** Совпадает с брейкпоинтом `lg` Tailwind, на котором раскладка становится двухпанельной. */
export const NARROW_QUERY = "(max-width: 1023px)";
/** Tailwind `sm` lower bound: below it the office shell uses its compact rail. */
export const PHONE_QUERY = "(max-width: 639px)";

function hasMatchMedia(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}

export function useIsNarrowViewport(query: string = NARROW_QUERY): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!hasMatchMedia()) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(
    () => (hasMatchMedia() ? window.matchMedia(query).matches : false),
    [query],
  );

  // Снимок для сервера: ширины там нет — отдаём десктопную раскладку.
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
